import { TranscriptionChunk } from "../interfaces/TranscriptionChunk";

export type Metadata = {
    name: string | null,
    type: string | null,
    size: number | null;

    duration_parts: {
        hours: number;
        minutes: number;
        seconds: number;
    } | null;
    width: number | null;
    height: number | null;
    format: string | null;
    videoCodec: string | null;
    aspectRatio?: number;
    audioCodec: string | null;
    fps: number | null;
    bitrate: number | null;
    sampleRate: number | null;
    audioChannels?: number | null;
    audioBitDepth?: number | null;
    duration: number | null;
    creationDate: string | null;
    lastModified: string | null;
    hash: string | null;
    transcoded?: boolean;
    path?: string | null; //for electron only
    frame_rate_num?: number | null;
    frame_rate_den?: number | null;
    duration_frames?: number | null;
    field_dominance?: string | null;
    component_width?: number | null;
    horizontal_subsampling?: number | null;
    vertical_subsampling?: number | null;
    video_line_map_1?: number | null;
    video_line_map_2?: number | null;
    aspect_ratio_num?: number | null;
    aspect_ratio_den?: number | null;
    tbr?: number | null;
    tbn?: number | null;
    start?: number | null;
    ffprobe_loaded?: boolean | null;
    timecodeStart?: string | null;
    pixelFormat?: string | null;
    colorMatrix?: string | null;
    videoBitDepth?: number | null;
    operational_pattern_ul?: string | null;
    uid?: string | null;
    generation_uid?: string | null;
    company_name?: string | null;
    product_name?: string | null;
    product_version?: string | null;
    product_uid?: string | null;
    umid?: string | null;
};


export const videoMimeTypes = [
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/quicktime',
    'video/x-msvideo',
    'video/x-matroska',
    'video/x-flv',
    'application/mxf',
    'video/mpeg',
    'video/3gpp',
    'video/x-ms-wmv',
    'video/x-m4v',
    'video/vnd.avi',
    'video/avi',
    'video/x-dv',
    'video/dv',
    'video/mp2t',
    'video/x-mpg',
    'video/x-mpeg',
    'video/divx',
    'video/x-ivf'
];


export function probeToMetadata(probe_string: string) {

    const probe = JSON.parse(probe_string);
    const video = probe.streams.find((s:any)=>s.codec_type==="video") ?? {};
    const audios = probe.streams.filter((s:any)=>s.codec_type==="audio");
  
  const fpsNum = video.avg_frame_rate || video.r_frame_rate || "0/1";
  const [num, den] = fpsNum.split("/").map(Number);
  const fps = den ? num / den : 0; // "24000/1001" → 23.976
  const [frameRateNum, frameRateDen] = (video.r_frame_rate || video.avg_frame_rate || "0/1")
    .split("/")
    .map(Number);
  
    const metadata: Metadata = {
      name:  null,
      type:  null,
      size:  Number(probe.format.size) || null,
      duration_parts: null,
      width: video.width || null,
      height: video.height || null,
      format: probe.format.format_name || null,
      videoCodec: video.codec_name || null,
      aspectRatio: video.width && video.height ? video.width/video.height : undefined,
      audioCodec: audios[0]?.codec_name ?? null,
      fps,
      bitrate: Number(probe.format.bit_rate) || null,
      sampleRate: Number(audios[0]?.sample_rate) || null,
      audioChannels: audios.reduce((n:any,s:any)=>n+Number(s.channels||0),0) || null,
      audioBitDepth: Number(video.bits_per_raw_sample) || null,
      duration: Number(probe.format.duration) || null,
      creationDate: probe.format.tags?.creation_time ?? null,
      lastModified: probe.format.tags?.modification_date ?? null,
      hash: null,
      // …досчитываем остальные поля как в вашем parseFFMpegMetadataFromLogs
      frame_rate_num: frameRateNum || null,
      frame_rate_den: frameRateDen || null,
      duration_frames: fps && probe.format.duration ? Math.round(fps*probe.format.duration) : null,
      pixelFormat: video.pix_fmt ?? null,
      colorMatrix: video.color_space?.toUpperCase() ?? null,
      operational_pattern_ul: probe.format.tags?.operational_pattern_ul ?? null,
      uid: probe.format.tags?.uid ?? null,
      generation_uid: probe.format.tags?.generation_uid ?? null,
      company_name: probe.format.tags?.company_name ?? null,
      product_name: probe.format.tags?.product_name ?? null,
      product_version: probe.format.tags?.product_version ?? null,
      product_uid: probe.format.tags?.product_uid ?? null,
      umid: probe.format.tags?.material_package_umid ?? null,
      ffprobe_loaded: true,
      // остальные nullable‑поля → null
    };
  
    // ── Расчёт aspect_ratio_num и aspect_ratio_den ──
    if (video.display_aspect_ratio) {
      const [arNum, arDen] = video.display_aspect_ratio.split(":").map(Number);
      metadata.aspect_ratio_num = arNum;
      metadata.aspect_ratio_den = arDen;
    } else if (metadata.width && metadata.height) {
      const gcd = (a: number, b: number): number => b ? gcd(b, a % b) : a;
      const div = gcd(metadata.width, metadata.height);
      metadata.aspect_ratio_num = metadata.width / div;
      metadata.aspect_ratio_den = metadata.height / div;
    }
 
    // ── duration_parts ──
    if (probe.format.duration) {
      const total = Number(probe.format.duration);
      metadata.duration_parts = {
        hours: Math.floor(total / 3600),
        minutes: Math.floor(total / 60) % 60,
        seconds: +(total % 60).toFixed(3),
      };
    }
 
    // ── field_dominance ──
    switch (video.field_order) {
      case 'progressive':
        metadata.field_dominance = 'FullFrame';
        break;
      case 'tt':
        metadata.field_dominance = 'UpperFieldFirst';
        break;
      case 'bb':
        metadata.field_dominance = 'LowerFieldFirst';
        break;
      default:
        break;
    }
 
    // ── Subsampling и component_width ──
    const pix = (video.pix_fmt ?? '').toLowerCase();
    if (pix.startsWith('yuv444')) {
      metadata.horizontal_subsampling = 1;
      metadata.vertical_subsampling = 1;
    } else if (pix.startsWith('yuv422')) {
      metadata.horizontal_subsampling = 2;
      metadata.vertical_subsampling = 1;
    } else if (pix.startsWith('yuv420')) {
      metadata.horizontal_subsampling = 2;
      metadata.vertical_subsampling = 2;
    }
    metadata.component_width = Number(video.bits_per_raw_sample ?? video.bits_per_sample) || null;
 
    // ── tbr, tbn и start ──
    const rate = (video.avg_frame_rate !== '0/0' ? video.avg_frame_rate : video.r_frame_rate) ?? '0/1';
    const [tbrNum, tbrDen] = rate.split("/").map(Number);
    metadata.tbr = tbrDen ? tbrNum / tbrDen : 0;
    metadata.tbn = Number(video.time_base?.split('/')[1] ?? null);
    metadata.start = Number(probe.format.start_time) || null;
 
    // ── timecodeStart ──
    metadata.timecodeStart = probe.format.tags?.timecode ?? video.tags?.timecode ?? null;
    return metadata;
  }

export function parseFFMpegMetadataFromLogs(ffmpeg_logs: string[] | string): Metadata {

    const infoString = typeof ffmpeg_logs==='string'? ffmpeg_logs: ffmpeg_logs.join('\n');

    console.log("infoString", infoString)


    // Регулярные выражения для извлечения информации
    const durationMatch = /Duration: (\d+):(\d+):(\d+\.\d+)/.exec(infoString);
    const widthMatch = /,\s(\d+)x(\d+)\s/.exec(infoString);
    const formatMatch = /Input #0, (\w+)/.exec(infoString);
    const videoCodecMatch = /Video: (\w+)/.exec(infoString);
    const audioCodecMatch = /Audio: (\w+)/.exec(infoString);
    const fpsMatch = /(\d+(\.\d+)?) fps/.exec(infoString);
    const bitrateMatch = /bitrate: (\d+) kb\/s/.exec(infoString);
    const sampleRateMatch = /(\d+) Hz/.exec(infoString);
    const creationDateMatch = /creation_time\s*:\s*(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z)/.exec(infoString);


    let metadata: Metadata = {
        size: null,
        width: null,
        height: null,
        format: null,
        videoCodec: null,
        audioCodec: null,
        fps: null,
        bitrate: null,
        sampleRate: null,
        duration: null,
        creationDate: null,
        lastModified: null,
        name: null, 
        type: null, 
        duration_parts: null, 
        hash: null
    };

    if (durationMatch) {
        const [, hours, minutes, seconds] = durationMatch;
        metadata.duration_parts = {
            hours: Number(hours),
            minutes: Number(minutes),
            seconds: Number(seconds),
        };
    }

    if (widthMatch) {
        const [, width, height] = widthMatch;
        metadata.width = Number(width);
        metadata.height = Number(height);
        metadata.aspectRatio = metadata.width / metadata.height;
    }

    if (formatMatch) {
        metadata.format = formatMatch[1];
    }

    if (videoCodecMatch) {
        metadata.videoCodec = videoCodecMatch[1];
    }

    if (audioCodecMatch) {
        metadata.audioCodec = audioCodecMatch[1];
    }

    if (fpsMatch) {
        metadata.fps = Number(fpsMatch[1]);
    }

    if (metadata.fps) {
        if (Math.abs(metadata.fps - 23.976) < 0.01) {
            metadata.frame_rate_num = 24000;
            metadata.frame_rate_den = 1001;
        } else {
            metadata.frame_rate_num = metadata.fps;
            metadata.frame_rate_den = 1;
        }
    }

    if (bitrateMatch) {
        metadata.bitrate = Number(bitrateMatch[1]);
    }

    if (sampleRateMatch) {
        metadata.sampleRate = Number(sampleRateMatch[1]);
    }

    // Determine total number of audio channels across all audio streams
    const channelMatches = Array.from(infoString.matchAll(/Audio:[^,]+,[^,]*,\s*(\d+)\s+channels?/g));
    if (channelMatches.length > 0) {
        metadata.audioChannels = channelMatches.reduce((sum, m) => sum + Number(m[1]), 0);
    } else {
        const channelTextMatch = /\b(mono|stereo|5\.1|7\.1)\b/.exec(infoString);
        if (channelTextMatch) {
            const map: Record<string, number> = {
                mono: 1,
                stereo: 2,
                '5.1': 6,
                '7.1': 8
            };
            metadata.audioChannels = map[channelTextMatch[1]];
        }
    }

    // Fallback for AAC formats like fltp
    if (!metadata.audioBitDepth && infoString.includes("fltp")) {
        metadata.audioBitDepth = 32;
    }

    const bitDepthMatch = /(\d+)-bit/.exec(infoString);
    if (bitDepthMatch) {
        metadata.audioBitDepth = Number(bitDepthMatch[1]);
    }

    if (metadata.duration_parts) {
        metadata.duration =
            metadata.duration_parts.hours * 3600 +
            metadata.duration_parts.minutes * 60 +
            metadata.duration_parts.seconds;
    }

    if (creationDateMatch) {
        metadata.creationDate = creationDateMatch[1];
    }
    
    const operationalPatternMatch = /operational_pattern_ul\s*:\s*([\da-fA-F\.\:]+)/.exec(infoString);
    if (operationalPatternMatch) {
        metadata.operational_pattern_ul = operationalPatternMatch[1];
    }
    
    const uidMatch = /uid\s*:\s*([^\n]+)/.exec(infoString);
    if (uidMatch) {
        metadata.uid = uidMatch[1].trim();
    }
    
    const generationUidMatch = /generation_uid\s*:\s*([^\n]+)/.exec(infoString);
    if (generationUidMatch) {
        metadata.generation_uid = generationUidMatch[1].trim();
    }
    
    const companyNameMatch = /company_name\s*:\s*([^\n]+)/.exec(infoString);
    if (companyNameMatch) {
        metadata.company_name = companyNameMatch[1].trim();
    }
    
    const productNameMatch = /product_name\s*:\s*([^\n]+)/.exec(infoString);
    if (productNameMatch) {
        metadata.product_name = productNameMatch[1].trim();
    }
    
    const productVersionMatch = /product_version\s*:\s*([^\n]+)/.exec(infoString);
    if (productVersionMatch) {
        metadata.product_version = productVersionMatch[1].trim();
    }
    
    const productUidMatch = /product_uid\s*:\s*([^\n]+)/.exec(infoString);
    if (productUidMatch) {
        metadata.product_uid = productUidMatch[1].trim();
    }
    
    const modificationDateMatch2 = /modification_date\s*:\s*([^\n]+)/.exec(infoString);
    if (modificationDateMatch2) {
        // Optionally store in lastModified if desired:
        metadata.lastModified = modificationDateMatch2[1].trim();
    }
    
    const umidMatch = /material_package_umid\s*:\s*(0x[0-9A-Fa-f]+)/.exec(infoString);
    if (umidMatch) {
        metadata.umid = umidMatch[1];
    }

    const tcMatch = /timecode\s*:\s*(\d{2}:\d{2}:\d{2}:\d{2})/.exec(infoString);
    if (tcMatch) metadata.timecodeStart = tcMatch[1];

    const pixMatch = /Video:.*?,\s*([a-z0-9]+(?:[0-9]{0,2}le)?)/i.exec(infoString);
    if (pixMatch) {
        console.log("pixMatch", pixMatch);
        metadata.pixelFormat = pixMatch[1];
        
        // Determine subsampling from pixel format
        const pixFmt = metadata.pixelFormat?.toLowerCase();
        if (pixFmt) {
            if (pixFmt.startsWith("yuv422")) {
                metadata.horizontal_subsampling = 2;
                metadata.vertical_subsampling = 1;
            } else if (pixFmt.startsWith("yuv420")) {
                metadata.horizontal_subsampling = 2;
                metadata.vertical_subsampling = 2;
            } else if (pixFmt.startsWith("yuv444")) {
                metadata.horizontal_subsampling = 1;
                metadata.vertical_subsampling = 1;
            }
        }
        
        const bitDepth = /(\d+)le$/.exec(pixMatch[1]);
        if (bitDepth) metadata.videoBitDepth = Number(bitDepth[1]);
    }

    const colorMatch = /(bt\d{3})/.exec(infoString);
    if (colorMatch) metadata.colorMatrix = colorMatch[1].toUpperCase();

    const startMatch = /start:\s*([\d\.]+)/.exec(infoString);
    if (startMatch) {
        metadata.start = Number(startMatch[1]);
    }

    const tbrMatch = /,\s*(\d+(?:\.\d+)?)\s*tbr/.exec(infoString);
    if (tbrMatch) {
        metadata.tbr = Number(tbrMatch[1]);
    }

    const tbnMatch = /,\s*([\d\.]+k?)\s*tbn/.exec(infoString);
    if (tbnMatch) {
        let tbnStr = tbnMatch[1];
        if (tbnStr.toLowerCase().endsWith('k')) {
            metadata.tbn = Number(tbnStr.slice(0, -1)) * 1000;
        } else {
            metadata.tbn = Number(tbnStr);
        }
    }

    if (metadata.duration && metadata.fps) {
        metadata.duration_frames = Math.round(metadata.duration * metadata.fps);
    }

    // Infer field dominance
    if (/progressive/i.test(infoString)) {
        metadata.field_dominance = "FullFrame";
    } else if (/interlaced/i.test(infoString)) {
        metadata.field_dominance = "UpperFieldFirst"; // default assumption
    }
    
    // Assume component width from pix_fmt
    if (metadata.videoBitDepth) {
        metadata.component_width = metadata.videoBitDepth;
    } else {
        metadata.component_width = 8; // default fallback
    }
    
    // Guess video line map only if progressive
    if (metadata.field_dominance === "FullFrame") {
        metadata.video_line_map_1 = 42;
        metadata.video_line_map_2 = 0;
    }

    metadata.ffprobe_loaded = true;

    if (metadata.width && metadata.height) {
        const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
        const divisor = gcd(metadata.width, metadata.height);
        metadata.aspect_ratio_num = metadata.width / divisor;
        metadata.aspect_ratio_den = metadata.height / divisor;
    }

    console.log("123 Metadata", metadata);
    return metadata;
}


export function secToTime(seconds: number, onClick?: (sec: number) => void): JSX.Element {

    const formatted = secToTimeString(seconds)

    return <span
        {...onClick ? {
            onClick: () => onClick(seconds),
            className: "border-b border-dashed border-blue-800 dark:border-blue-200 text-blue-800 dark:text-blue-200 cursor-pointer"
        } : {}}
    >
        {formatted}
    </span>
}

export async function generateHash(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
    return hashHex;
}


export function secToTimeString(seconds:number, include_millis:boolean=false): string {
    if (Number.isNaN(seconds)) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = include_millis ? Math.floor(seconds % 60) : Math.ceil(seconds % 60);
    const milliseconds =  Math.round((seconds % 1) * 1000);


    const hoursStr = hours.toString().padStart(2, '0');
    const minutesStr = minutes.toString().padStart(2, '0');
    const secsStr = secs.toString().padStart(2, '0');
    const msStr = milliseconds.toString().padStart(3, '0');
    return `${hoursStr}:${minutesStr}:${secsStr}` + (include_millis ? `.${msStr}` : '');
}

export const parseTime = (timeString: string): number => {
    const [h, m, s] = timeString.split(':').map(Number);
    return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
};

export const convertSubtitlesToWebVTT = (chunks: TranscriptionChunk[]) => {
    let vttContent = "WEBVTT\n\n";
  
    chunks.forEach((subtitle, index) => {
      if (subtitle.timestamp) {
        const { begin, end } = subtitle.timestamp;
        const text = subtitle.text;
        vttContent += `${index + 1}\n`; // Номер субтитра
        vttContent += `${secToTimeString(begin,true)} --> ${secToTimeString(end,true)}\n`; 
        vttContent += `${text}\n\n`; 
      }
    });
  
    //console.log("subtitles",vttContent);
    return vttContent;
};
